import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import UploadField from "./UploadField";
import { Plus, Trash2 } from "lucide-react";

const EMPTY = { name: "", role: "", specialties: "", photo: "" };

export default function TeamPanel() {
  const [team, setTeam] = useState([]);
  const [adding, setAdding] = useState(null);

  const load = () => api.get("/team").then((res) => setTeam(res.data.team.map((m) => ({ ...m, specialtiesText: m.specialties.join(", ") })))).catch(() => {});
  useEffect(() => { load(); }, []);

  const updateLocal = (id, key, value) => setTeam((t) => t.map((m) => (m.id === id ? { ...m, [key]: value } : m)));

  const save = async (m) => {
    try {
      await api.patch(`/admin/team/${m.id}`, {
        name: m.name,
        role: m.role,
        photo: m.photo,
        specialties: m.specialtiesText.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success(`${m.name} enregistrée`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`Retirer ${m.name} de l'équipe ?`)) return;
    await api.delete(`/admin/team/${m.id}`).catch(() => toast.error("Suppression impossible"));
    toast.success("Membre retiré");
    load();
  };

  const add = async () => {
    try {
      await api.post("/admin/team", {
        name: adding.name,
        role: adding.role,
        photo: adding.photo,
        specialties: adding.specialties.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Membre ajouté");
      setAdding(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const inputCls = "mt-1 h-11 rounded-none border-black/10 bg-transparent text-sm font-light";

  const MemberCard = ({ m, onChange, onSave, isNew }) => (
    <div className="border border-black/5 bg-[var(--brand-bg)] p-6" data-testid={isNew ? "team-new-card" : `team-edit-${m.id}`}>
      <UploadField label="Photo" value={m.photo} onChange={(v) => onChange("photo", v)} testid={isNew ? "team-new-photo" : `team-photo-${m.id}`} />
      <div className="mt-4 grid gap-3">
        <div>
          <label className="text-xs uppercase tracking-[0.2em] text-[var(--brand-muted)]">Prénom</label>
          <Input value={m.name} onChange={(e) => onChange("name", e.target.value)} className={inputCls} data-testid={isNew ? "team-new-name" : `team-name-${m.id}`} />
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.2em] text-[var(--brand-muted)]">Rôle</label>
          <Input value={m.role} onChange={(e) => onChange("role", e.target.value)} className={inputCls} placeholder="Experte cils…" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.2em] text-[var(--brand-muted)]">Spécialités (séparées par des virgules)</label>
          <Input value={isNew ? m.specialties : m.specialtiesText}
                 onChange={(e) => onChange(isNew ? "specialties" : "specialtiesText", e.target.value)}
                 className={inputCls} placeholder="Cils, Sourcils" data-testid={isNew ? "team-new-specialties" : `team-specialties-${m.id}`} />
          <p className="mt-1 text-[11px] italic text-[var(--brand-muted)]">Doit correspondre aux catégories de prestations : Cils, Sourcils, Tatouage…</p>
        </div>
        <button onClick={onSave}
                className="mt-2 rounded-full bg-[var(--brand-dark)] py-3 text-xs uppercase tracking-[0.2em] text-[var(--brand-bg)] transition-transform duration-200 hover:scale-[0.98]"
                data-testid={isNew ? "team-new-save" : `team-save-${m.id}`}>
          {isNew ? "Ajouter à l'équipe" : "Enregistrer"}
        </button>
      </div>
    </div>
  );

  return (
    <div className="mt-10" data-testid="team-panel">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-3xl text-[var(--brand-dark)]">Équipe</h2>
        <button onClick={() => setAdding({ ...EMPTY })}
                className="flex items-center gap-2 rounded-full bg-[var(--brand-dark)] px-6 py-3 text-xs uppercase tracking-[0.2em] text-[var(--brand-bg)] transition-transform duration-200 hover:scale-95"
                data-testid="team-add-btn">
          <Plus size={14} /> Ajouter
        </button>
      </div>
      <div className="mt-8 grid gap-6 lg:grid-cols-2">
        {team.map((m) => (
          <div key={m.id} className="relative">
            <MemberCard m={m} onChange={(k, v) => updateLocal(m.id, k, v)} onSave={() => save(m)} />
            <button onClick={() => remove(m)}
                    className="absolute right-4 top-4 flex items-center gap-1.5 rounded-full border border-[#993333]/30 px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] text-[#993333] transition-colors hover:bg-[#993333] hover:text-white"
                    data-testid={`team-delete-${m.id}`}>
              <Trash2 size={11} /> Retirer
            </button>
          </div>
        ))}
        {adding && (
          <MemberCard m={adding} isNew onChange={(k, v) => setAdding({ ...adding, [k]: v })} onSave={add} />
        )}
      </div>
    </div>
  );
}
